/* eslint-disable max-params */
import React, {createContext, FC, useContext, useRef, useState} from "react";
import {ColumnProps} from "antd/lib/table";
import moment, {Moment} from "moment";
import {Button, Checkbox, InputRef, message, Modal, notification, Popconfirm, Popover, Space, Spin, Table, Tag, Tooltip, Typography} from "antd";
import {CheckboxChangeEvent} from "antd/lib/checkbox";
import {
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    HistoryOutlined,
    InfoCircleTwoTone,
    LinkOutlined,
    MoreOutlined,
    PushpinOutlined,
    ReloadOutlined,
    RollbackOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import ButtonGroup from "antd/lib/button/button-group";
import {useMutation, BaseMutationOptions} from "@apollo/client";
import {blue, red} from "@ant-design/colors";
import css from "../../views/TableColumnRenderers/TableColumnRenderers.less";
import {
    ArrayDataOptions,
    Booker,
    Booking,
    CourseLesson,
    GetBookingByIdDocument,
    GetBookingByIdWithCourseLessonBookingsDocument,
    GetPaidScheduledPaymentsDocument,
    GetScheduledPaymentsQuery,
    GetUnpaidScheduledPaymentsDocument,
    Maybe,
    PaymentTransferType,
    ScheduledPayment,
    ScheduledPaymentType,
    useClearManuallyAdjustedMutation,
    useDeleteScheduledPaymentMutation,
    useRestoreScheduledPaymentMutation,
    useSetNewPaymentDoneDateMutation,
    useSetScheduledPaymentPaidMutation,
    useSetScheduledPaymentUnpaidMutation,
    useSetSepaClearanceMutation,
    PaymentProvider,
} from "../../generated/graphql";
import {
    renderUnknown,
    renderDate,
    renderBookingNumber,
    renderCourseType,
    renderCourseNumber,
    renderTextStandard,
    renderFirstCourseLesson,
    renderPaymentType,
    renderDueDateColored,
    renderBookerFullName,
    renderAttendeeFullName,
    RenderAmount,
    renderPercent,
    renderInvoiceTitle,
    renderContinuousText,
} from "../../views/TableColumnRenderers/TableColumnRenderers";
import PaymentMethodBadge from "../../views/PaymentMethodBadge/PaymentMethodBadge";
import {classifyMollieDispatchError} from "../../helpers/mollieDispatchError";
import {noteHasBankReference} from "../../../shared/lib/bankReference";
import i18n from "../../services/i18n";
import {ERASED_ACTION_HINT, erasedRecordMessage} from "../../helpers/erasedRecordMessage";
import {QueryItem} from "../../types";
import RenderInvoicePreview from "../../views/RenderInvoicePreview/RenderInvoicePreview";
import {URL_COURSES_DETAILS_ID} from "../../models/url";
import CalendarPopover from "../../views/CalendarPopover/CalendarPopover";
import columnSortFilterTest, {AutoCompleteOptions, InputType} from "../../helpers/columnSortFilterTest";
import FilterDropdownPresence from "../../helpers/tableHelpers/FilterDropdownPresence";
import {NDN_FILTER_ABSENT, NDN_FILTER_PRESENT} from "../../../shared/const/nonDisclosureNoticeFilter";
import {useCurrency} from "../../helpers/currencyHelper";
import {CurrencyShort, renderAmount as renderAmountHelper} from "../../helpers/renderAmount";
import useHighestRole, {useIsBetaTester} from "../../helpers/useHighestRole";
import useBankSuggestionForMarkPaid from "./BankImport/useBankSuggestionForMarkPaid";
import roles from "../../../shared/models/roles";
import {options} from "../../views/PanelCardPayments/AddNamedScheduledPaymentForm";
import {RETRY_MOLLIE_PAYMENT} from "../settings/mollie.graphql";
// Phase 8 (RETRY-04, D-11 zweite UI-Stelle): RetryButton fuer chargebacked-Raten
import {RetryButton as ChargebackRetryButton} from "../mollieRetryQueue/RetryButton";
// Phase 13 (DISP-06 + DISP-08) — Countdown-Zelle fuer "Link gueltig bis"-Column.
import CountdownCell from "../../views/CountdownCell/CountdownCell";
// Phase 13 (DISP-01, DISP-02, DISP-08) — GraphQL-Documents fuer Admin-Dispatch.
import {SEND_MOLLIE_PAYMENT_LINK_FROM_ADMIN, DETECT_BUNDLE_CONFLICTS_FROM_ADMIN} from "./mollie.graphql";
import {formatMollieBanktransferLastValidDay} from "../../../shared/lib/mollieBanktransferDates";
// Phase 14 / Plan 14-05 (D-13, D-23) — Standalone-Refund-Button + refund-Badge-States.
import MollieRefundButton from "../../views/MollieRefundButton/MollieRefundButton";
import SurplusMolliePaymentBadge from "../../views/SurplusMolliePaymentBadge/SurplusMolliePaymentBadge";
import type {RefundPanelRow} from "../../views/RefundPanel/RefundPanel";
import {getDisplayedPaymentMethodType} from "../../helpers/getDisplayedPaymentMethodType";
import DunningStatusCell, {dunningStatusOf} from "../invoice/SettlementDetails/BookingDunnings/DunningStatusCell";
import DunningFeeDeleteButton from "../invoice/SettlementDetails/BookingDunnings/DunningFeeDeleteButton";
import DateInput from "../../views/DateInput/DateInput";

/**
 * Phase 8 — Non-Retriable Reason-Codes (Mandat wird deaktiviert, kein Retry).
 * Whitelist aus server/mollie/retryPolicy.ts (Plan 08-01).
 */
const PHASE_8_NON_RETRIABLE_CODES = new Set(["AC04", "AC01", "AC06", "MD01"]);

/**
 * Extrahiert einen potenziellen Mollie-Reason-Code aus einer Error-Message,
 * z.B. "Chargeback reason: AC04 — Konto geschlossen" → "AC04".
 * Fallback: undefined.
 */
function parseReasonCodeFromErrorMessage(msg?: string | null): string | undefined {
    if (!msg) return undefined;
    const match = msg.match(/\b(AC0[146]|AM04|MD0[1-7]|MS03)\b/);
    return match ? match[1] : undefined;
}

type ExtraColumns = {
    // actions: "actions";
    setPaidAction: "setPaidAction";
    setUnPaidAction: "setUnPaidAction";
    bookingNumber: "bookingNumber";
    courseType: "courseType";
    courseNumber: "courseNumber";
    booker: "booker";
    attendee: "attendee";
    city: "city";
    location: "location";
    firstCourseLesson: "firstCourseLesson";
    paymentType: "paymentType";
    paymentMethod: "paymentMethod";
    sepaClearance: "sepaClearance";
    vat: "vat";
    invoiceDate: "invoiceDate";
    noticeStaff: Booking["noticeStaff"];
    invoiceTitle: ScheduledPayment["title"];
    scheduledPaymentNote: ScheduledPayment["note"];
    // sc-3040 — Sperrvermerk des Bookers (booking.attendee.booker.nonDisclosureNotice).
    // Server-seitig sort-/filterbar via scheduledPayment.{addFilterToQuery,addSortersToQuery}.
    nonDisclosureNotice: Booker["nonDisclosureNotice"];
    paymentSource: "paymentSource";
    mollieStatus: "mollieStatus";
    // Phase 13 / DISP-06 + DISP-08 — Countdown-Spalte "Link gueltig bis".
    // Liest `record.currentMolliePaymentAttempt?.linkExpiresAt`. Wird von Plan 13-03
    // Backend-seitig bereitgestellt (additiv); bis dahin lesen die Zellen safely
    // via Optional-Chaining auf undefined.
    mollieLinkExpiresAt: "mollieLinkExpiresAt";
    // Phase 13 / DISP-01 + DISP-04 — Row-Action "Zahlungslink senden".
    // Feature-Flag + Bundle-Conflict-Set werden via MolliePaymentLinkDispatchContext
    // uebergeben (siehe Provider-Export unten). Die Column-Rendering-Logik rendert
    // MolliePaymentLinkDispatchButton + ggf. MollieBundleConflictBadge inline.
    mollieDispatchAction: "mollieDispatchAction";
    // Phase 14 / Plan 14-05 (D-23) — Interim-Badge-Spalte fuer Refund-Status.
    // Rendert nur echte Provider-Erstattungen; interne Guthaben bleiben unsichtbar.
    // Phase 16 BADGE-01 uebernimmt diese States in die 8-State-Refaktorierung.
    refundStatus: "refundStatus";
    // Phase 16 / AUDIT-02 — Row-Action-Spalte für Payment-Action-Timeline-Drawer.
    // Icon HistoryOutlined öffnet rechts-Drawer mit LazyQuery getPaymentActionLogs.
    // Callback wird via PaymentActionTimelineContext gesetzt (siehe unten); ohne
    // Callback wird das Icon nicht gerendert — verhindert tote UI in Call-Sites
    // außerhalb des OpenPaymentsList-Providers.
    paymentActionTimeline: "paymentActionTimeline";
    // B-223 Followup — Status-Spalte mit Tag "Aktiv"/"Gelöscht" + Restore-Action
    // bei Tombstones (deletedAt != null). Sichtbar by default, weil sie im
    // Default-Fall ein leichtgewichtiges grünes "Aktiv"-Tag rendert und nur bei
    // eingeblendeten Tombstones (UI-Toggle "Gelöschte anzeigen") die rote
    // Restore-Variante mit Popover/Audit-Zeile einblendet.
    tombstoneStatus: "tombstoneStatus";
    // #1517 — Mahnstufe der Buchung (nur Offene Zahlungen, Flag featureDunning)
    dunningLevel: "dunningLevel";
};

/**
 * Phase 13 (DISP-04, DISP-08) — Context fuer Dispatch-Column.
 *
 * Wird vom OpenPaymentsList-Container gesetzt (featureMollie + bundleConflict-Set
 * aus detectBundleConflictsFromAdmin). Default: Feature aus + leeres Set — damit
 * andere Call-Sites (z.B. DonePaymentsList) sicher ohne Kontext rendern.
 */
export type MolliePaymentLinkDispatchContextValue = {
    featureMollie: boolean;
    conflictingSpIds: Set<string>;
    refetch?: () => void;
};

export const MolliePaymentLinkDispatchContext = createContext<MolliePaymentLinkDispatchContextValue>({
    featureMollie: false,
    conflictingSpIds: new Set<string>(),
    refetch: undefined,
});

/**
 * Phase 16 / AUDIT-02 — Context für Timeline-Drawer-Opener.
 *
 * Wird vom OpenPaymentsList-Container gesetzt (ruft `onOpenTimeline(spId)` →
 * öffnet PaymentActionTimelineDrawer). Default: undefined — damit andere
 * Call-Sites (z.B. DonePaymentsList) die Column ohne tote Icons rendern.
 * Wenn `onOpenTimeline` nicht gesetzt ist, rendert die Column-Renderer
 * bewusst nichts (statt eines Icons mit no-op onClick).
 */
export type PaymentActionTimelineContextValue = {
    onOpenTimeline?: (scheduledPaymentId: string) => void;
};

export const PaymentActionTimelineContext = createContext<PaymentActionTimelineContextValue>({
    onOpenTimeline: undefined,
});

// const invoiceTitleAutoCompleteOptions: AutoCompleteOptions = [
//     {label: "Rechnung", value: "Rechnung"},
//     {lablel: "Buchungsbestätigung", value: "Buchungsbestätigung"},
//     {label: "Stornierung", value: "Stornierung"},
//     {label: "Wartelistenbestätigung", value: "Wartelistenbestätigung"},
//     {label: "Storno-Rechnung", value: "Storno-Rechnung"},
//     {label: "Kurserinnerung", value: "Kurserinnerung"},
//     {label: "Mahnung", value: "Mahnung"},
// ];

const invoiceTitleAutoCompleteOptions: AutoCompleteOptions = options.map((option) => ({
    label: option.label,
    value: option.label,
}));

/** „Mit Konto abgeglichen“: Die Rate trägt die Bankreferenz als Beleg (Bankabgleich oder externe Zahlung mit Referenz). */
export const bankReconciledForRate = (rate: {paymentDone?: Date | string | null; note?: string | null}): boolean => Boolean(rate.paymentDone) && noteHasBankReference(rate.note);

export type TableType = GetScheduledPaymentsQuery["scheduledPayments"]["items"][0] & ExtraColumns;
// export type TableType = GetCoursesQuery["courses"][0] & ExtraColumns;

export type GetScheduledPaymentsQueryItem = QueryItem<GetScheduledPaymentsQuery["scheduledPayments"]["items"]>;
// export type GetCoursesQueryItem = QueryItem<GetCoursesQuery["courses"]>;

export const rowKey: (course: TableType) => TableType["id"] = (scheduledPayment) => scheduledPayment.id;

/** Spalten, die nur mit featureMollie angeboten werden (Standardspalten der offenen Posten und Spaltenwähler) */
export const MOLLIE_COLUMNS: Array<Extract<keyof TableType, string>> = [
    "paymentSource",
    "mollieStatus",
    // Row-Action-Column (Button "Zahlungslink senden")
    "mollieDispatchAction",
    // Phase 14 / Plan 14-05 (D-23) — refundStatus rendert bei partial-refund-SPs, die wieder als "offen" erscheinen (Restbetrag offen).
    "refundStatus",
    // Phase 16 / AUDIT-02 — Timeline-Drawer-Opener; die meisten Events (method_changed, link_sent, refund_*, chargeback_*) sind Mollie-spezifisch.
    "paymentActionTimeline",
    // D-14: default-hidden, nur über den Spaltenwähler aktivierbar
    "mollieLinkExpiresAt",
];

/**
 * #658 (ADR 0006): Übertrags-Marker nur für eine VOLL getilgte Rate anzeigen.
 *
 * Gleiche Semantik wie serverseitig `resolveMethodLabelForScheduledPayment`
 * (`sp.paymentDone != null`): eine teilbezahlte Rate behält ihren Marker in der
 * DB, wird aber weiter über die gewählte Zahlart eingezogen — die Zahlart-Spalte
 * muss dort also die Zahlart zeigen, nicht den Übertrag. Auf der Rechnung
 * erscheint der Übertrag in dem Fall als `partialPaidNote`.
 *
 * Bewusst als eigene Funktion, damit diese Regel testbar ist: sie existiert
 * zwangsläufig doppelt (Server und Client haben getrennte Typen), und genau
 * dieses Auseinanderlaufen war der Fehler.
 */
/**
 * Notiz-Zusatz für eine noch offene Rate, die der Übertrag aus einer Umbuchung teilweise
 * gedeckt hat: „Teilzahlung 16,66 € per Übertrag aus B-892" (Abnahme 22.09.2026). Reine
 * Anzeige — die gespeicherte Notiz des Postens bleibt unverändert.
 */
export const transferPartialNoteForRate = (rate: {paymentDone?: Date | string | null; transferFromBookingNumber?: string | null; transferPaidAmount?: number | null; openAmount?: number | null; paymentAmount?: number | string | null}, currency?: CurrencyShort): string | null => {
    if (rate.paymentDone != null || !rate.transferFromBookingNumber || !(Number(rate.transferPaidAmount ?? 0) > 0)) return null;
    // Nur bei echter Teilzahlung (0 < offen < Betrag) — wie `renderPartialPaidNote` auf der
    // Rechnung. Nach „als unbezahlt markieren" ist die Rate wieder ganz offen, Marker und
    // Betrag bleiben aber stehen (Review #993).
    const open = Number(rate.openAmount);

    if (rate.openAmount == null || !(open > 0) || open >= Number(rate.paymentAmount ?? 0)) return null;

    return `Teilzahlung ${renderAmountHelper({amount: Number(rate.transferPaidAmount), currency, short: true})} per Übertrag aus ${rate.transferFromBookingNumber}`;
};

export const transferTagForRate = (rate: {paymentDone?: Date | string | null; transferFromBookingNumber?: string | null; transferPaidAmount?: number | null; paymentAmount?: number | string | null}): string | null => {
    if (rate.paymentDone == null || !rate.transferFromBookingNumber) return null;
    // Marker steht auch auf teilweise gedeckten Raten; „per Übertrag bezahlt" nur, wenn der
    // Übertrag die ganze Rate deckt (Altbestand ohne Betrag: Marker nur bei voller Deckung).
    const fullyCovered = rate.transferPaidAmount == null || Number(rate.transferPaidAmount) + 0.005 >= Number(rate.paymentAmount ?? 0);

    return fullyCovered ? rate.transferFromBookingNumber : null;
};

export const useHandleColumns = (
    columnsToShow: Array<Extract<keyof TableType, string>>,
    options: Maybe<ArrayDataOptions> | undefined,
    setOptions: (options: ArrayDataOptions) => void,
    editLink: string,
    detailsLink: string,
    // WP0b: Vollständige Zeilenliste für die Retry-Bündelung (gleiche Rechnung,
    // gleiche Fälligkeit). Optional — ohne Liste verhält sich der Retry wie bisher.
    tableData: Array<TableType> | undefined,
    isExpandable?: boolean,
    activeFilters?: ArrayDataOptions["filter"],
    refetch?: () => void,
    editPaymentOpen?: boolean,
    setEditPaymentOpen?: (editPaymentOpen: boolean) => void,
    setRecordToEdit?: (record: TableType) => void,

    // eslint-disable-next-line max-params
) => {
    const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
    const shownColumns: Array<ColumnProps<TableType>> = [];

    const currency = useCurrency();

    const getColumnsToShow = () => {
        columns.forEach((col) => {
            if (columnsToShow.some((key) => key === col.dataIndex)) {
                shownColumns.push(col);
            }
        });
        moveActionColumnToLastPosition(shownColumns);
    };

    const moveActionColumnToLastPosition = (shownColumns: Array<ColumnProps<TableType>>) => {
        shownColumns.forEach((col) => {
            if (col.fixed === "right") {
                shownColumns.push(shownColumns.splice(shownColumns.indexOf(col), 1)[0]);
            }
        });
    };

    const columns: Array<ColumnProps<TableType>> = [];

    const column: ColumnProps<TableType> = {
        title: "",
        fixed: false,
    };

    const inputRef = useRef<InputRef>(null);

    const ref = inputRef.current?.focus({
        cursor: "all",
    });

    columnsToShow.forEach((col: keyof TableType) => {
        const c = {...column};

        const colTitle = i18n.views.TableColumnTitles[col as keyof typeof i18n.views.TableColumnTitles]();

        const rawTitle = colTitle;
        // Nur in einen pre-line-span wrappen, wenn der Titel tatsaechlich umgebrochen werden kann (enthaelt ein Leerzeichen).
        // Andernfalls blockiert der Block-span das th-Layout und verdraengt Sort-/Filter-Icons (z.B. bei "Zahlungsquelle").
        const title = rawTitle.length <= 12 || !rawTitle.includes(" ") ? rawTitle : <span style={{whiteSpace: "pre-line"}}>{rawTitle.replace(" ", "\n")}</span>;

        const standardEllipsis = false;

        c.ellipsis = standardEllipsis;
        c.title = <Tooltip title={title}>{title}</Tooltip>;
        c.dataIndex = col;
        c.width = 100 + colTitle.length * 2;

        let path = c.dataIndex;

        const sortFilter = ({
            dataIndex,
            disabled,
            type,
            selectOptions,
            activeFilters,
            autoCompleteOptions,
        }: {
            dataIndex: string;
            disabled?: boolean;
            type: InputType;
            selectOptions?: Array<{
                label: string;
                value: string;
            }>;
            activeFilters?: ArrayDataOptions["filter"] | undefined;
            autoCompleteOptions?: AutoCompleteOptions;
        }) =>
            // eslint-disable-next-line max-params
            {
                Object.entries(
                    columnSortFilterTest({
                        dataIndex,
                        type,
                        selectOptions,
                        options,
                        setOptions,
                        dropdownOpen,
                        setDropdownOpen,
                        autoCompleteOptions,
                        table: "payment", // FIX: Korrekte Tabelle für Payments
                    }),
                ).map(([key, value]) => {
                    if (key !== "filteredValue") {
                        // @ts-ignore
                        return (c[key] = disabled ? null : value);
                    }

                    return null;
                });
            };

        switch (col) {
            case "setPaidAction":
                c.fixed = isExpandable ? false : "right";
                c.width = 10;
                c.align = "center";
                c.render = (text, record: TableType) => renderActions(record, editLink, detailsLink, "setAsPaid", refetch, editPaymentOpen, setEditPaymentOpen, setRecordToEdit);
                break;
            case "setUnPaidAction":
                c.fixed = isExpandable ? false : "right";
                c.width = 10;
                c.align = "center";
                c.render = (text, record: TableType) => renderActions(record, editLink, detailsLink, "setAsUnpaid", refetch, editPaymentOpen, setEditPaymentOpen, setRecordToEdit);
                break;
            case "bookingNumber":
                c.fixed = "left";
                path = "booking.bookingNumber";
                c.width = 120;
                c.render = (text, record: TableType) =>
                    renderBookingNumber({
                        bookingId: record.booking.id,
                        bookingNumberPrefix: record.booking.bookingNumberPrefix,
                        bookingNumber: record.booking.bookingNumber,
                        bookingType: record.booking.bookingType,
                        className: css.primary,
                        canceled: Boolean(record.booking.canceled),
                        isSettled: Boolean(record.booking.invoice),
                        allowLessonBooking: Boolean((record.booking.courseLessons?.[0]?.course as {allowLessonBooking?: boolean} | undefined)?.allowLessonBooking),
                        hasMolliePayment: record.paymentProvider === PaymentProvider.Mollie,
                    });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = true;
                break;
            case "courseType":
                // path = "courseLessons.0.course.courseType.name";
                c.render = (text, record: TableType) =>
                    renderCourseType({
                        name: record.booking.courseLessons?.[0]?.course?.courseType?.name ?? "",
                        color: record.booking.courseLessons?.[0]?.course?.courseType?.color ?? "",
                    });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "courseNumber":
                c.render = (text, record: TableType) =>
                    renderCourseNumber({
                        courseNumber: record.booking.courseLessons?.[0]?.course?.prefixedCourseNumber ?? undefined,
                        courseId: record.booking.courseLessons?.[0]?.course?.id ?? "",
                        detailsLink: URL_COURSES_DETAILS_ID.replace(":id", record.booking.courseLessons?.[0]?.course?.id ?? ""),
                    });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    // Multi-Value-Tag-Input: mehrere Kursnummern gleichzeitig
                    // filtern. Backend (scheduledPayment.addFilterToQuery ->
                    // precise courseNumber-Matching) OR-verknuepft bereits das
                    // ganze filter.values-Array.
                    type: "searchMultiple",
                });
                c.sorter = false;
                break;
            case "booker":
                c.render = (text, record: TableType) =>
                    renderBookerFullName(record.booking.attendee?.booker?.id, record.booking.attendee?.booker?.firstname, record.booking.attendee?.booker?.lastname);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "attendee":
                c.render = (text, record: TableType) => renderAttendeeFullName(record.booking.attendee?.booker?.id, record.booking.attendee?.firstname, record.booking.attendee?.lastname);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "city":
                c.render = (text, record: TableType) => renderTextStandard(record.booking.courseLessons?.[0]?.location?.city);
                break;
            case "location":
                c.render = (text, record: TableType) => renderTextStandard(record.booking.courseLessons?.[0]?.location?.name);
                break;
            case "firstCourseLesson":
                c.render = (text, record: TableType) =>
                    renderFirstCourseLesson({
                        courseLessons: (record.booking.courseLessons ?? []) as Array<CourseLesson>,
                    });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "date",
                });
                c.sorter = false;
                break;
            case "createDate":
                c.align = "center";
                c.render = (text, record: TableType) => renderDate(record.createDate);

                break;
            case "paymentDue":
                c.align = "center";
                c.render = (text, record: TableType) => renderDueDateColored(record.paymentDue);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "date",
                });
                c.sorter = false;
                break;
            case "paymentDone":
                c.align = "center";
                c.render = (text, record: TableType) =>
                    record.paymentDone ? (
                        <Space size={4}>
                            {renderDate(record.paymentDone)}
                            {bankReconciledForRate(record) && (
                                <Tooltip title={i18n.containers.payments.PaymentsList.bankReconciled()}>
                                    <Tag color="cyan" style={{marginRight: 0}}>
                                        {i18n.containers.payments.PaymentsList.bankReconciledShort()}
                                    </Tag>
                                </Tooltip>
                            )}
                        </Space>
                    ) : (
                        renderDueDateColored(record.paymentDue, i18n.containers.payments.PaymentsList.notPaid())
                    );
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "date",
                });
                // c.sorter = false;
                break;
            case "paymentAmount":
                c.render = (text, record: TableType) => ({
                    displayName: "paymentAmount",
                    children:
                        record.openAmount && record.openAmount > 0 && record.paymentAmount !== record.openAmount ? (
                            <Tooltip title={`Es wurde bereits mindestens eine Teilzahlung getätigt. Der Originalbetrag lag bei ${record.paymentAmount} ${currency.short}`}>
                                <Space
                                    size={5}
                                    style={{
                                        fontWeight: 650,
                                        color: blue[7],
                                    }}
                                >
                                    <InfoCircleTwoTone />
                                    <Space size={2}>
                                        <RenderAmount
                                            amount={record.openAmount ?? record.paymentAmount}
                                            style={{
                                                fontWeight: 650,
                                                color: blue[7],
                                            }}
                                        />
                                        <span>{"*"}</span>
                                    </Space>
                                </Space>
                            </Tooltip>
                        ) : (
                            <>
                                <RenderAmount amount={record.paymentAmount} style={{fontWeight: 550}} />
                            </>
                        ),
                });
                break;
            case "openAmount":
                c.render = (text, record: TableType) => ({
                    displayName: "openAmount",
                    children: <RenderAmount amount={record.openAmount ?? record.paymentAmount} />,
                });
                break;
            case "invoice":
                // path = "invoice.invoiceNumber";
                c.align = "center";
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    const getInvoiceData = (record: TableType) => {
                        if (!record.invoice) {
                            return undefined;
                        }

                        const {invoiceNumberPrefix, invoiceNumber, id} = record.invoice;

                        return {
                            id,
                            invoiceNumber,
                            invoiceNumberPrefix,
                            // canceled: Boolean(record.canceled !== null),
                            isReversalInvoice: false,
                        };
                    };

                    const invoiceData = getInvoiceData(record);

                    if (invoiceData === undefined) {
                        return <div>{"-"}</div>;
                    }

                    return <RenderInvoicePreview invoiceData={invoiceData} />;
                };
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = true;
                break;
            case "paymentType":
                c.align = "center";
                c.render = (text, record: TableType) => renderPaymentType(record.booking.paymentType);
                break;
            case "paymentMethod":
                c.align = "center";
                c.render = (text, record: TableType) => {
                    // Bei Mollie-Zahlungen zeigen wir die tatsächlich genutzte Methode
                    // (Kreditkarte, PayPal, etc.) statt der Default-Buchungs-PM.
                    // Ohne Mollie-Kontext fällt die Darstellung auf die Buchungs-PM zurück.
                    const isMollie = record.paymentProvider === PaymentProvider.Mollie && Boolean(record.mollieMethod);
                    // #658 (ADR 0006): per Übertrag getilgte Rate — siehe transferTagForRate.
                    const transferFrom = transferTagForRate(record as TableType & {transferFromBookingNumber?: string | null; transferPaidAmount?: number | null});

                    if (transferFrom) {
                        return (
                            <Tag color="purple" style={{margin: 0}}>
                                {i18n.containers.payments.PaymentsListColumns.transferFrom({bookingNumber: transferFrom})}
                            </Tag>
                        );
                    }

                    return (
                        <PaymentMethodBadge
                            type={record.paymentMethod ? getDisplayedPaymentMethodType(record.paymentMethod) : undefined}
                            paymentProvider={isMollie ? "Mollie" : undefined}
                            mollieMethod={isMollie ? record.mollieMethod : undefined}
                        />
                    );
                };

                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {
                            label: "Überweisung",
                            value: PaymentTransferType.BankTransfer,
                        },
                        {
                            label: "Lastschrift",
                            value: PaymentTransferType.SepaDebit,
                        },
                        {label: "Bar", value: PaymentTransferType.Cash},
                    ],
                });
                c.sorter = false;
                break;
            case "sepaClearance":
                c.align = "center";
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) =>
                    record.paymentMethod?.sepaAccountholder ? (
                        <SepaClearanceCheckbox record={record} refetch={refetch} />
                    ) : (
                        <Tooltip title="Bezahlmethode ist nicht Lastschrift">{renderTextStandard("-")}</Tooltip>
                    );
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {label: "Freigegeben", value: "true"},
                        {label: "Nicht freigegeben", value: "false"},
                    ],
                });
                c.sorter = false;
                break;
            case "vat":
                c.render = (text, record: TableType) => ({
                    displayName: "vat",
                    children: <div>{renderPercent(record.booking.vat)}</div>,
                });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {label: "20%", value: "0.2"},
                        {label: "19%", value: "0.19"},
                        {label: "7%", value: "0.07"},
                        {label: "0%", value: "0.0"},
                    ],
                });
                c.sorter = false;
                break;
            case "title":
                c.title = i18n.views.TableColumnTitles.invoiceTitle();
                c.fixed = false;
                // B-223 Wave 7 — Indikator fuer manuell geschuetzte Posten links vom Titel.
                // Rendert nur wenn manuallyAdjustedAt!=null && deletedAt==null (Tombstones
                // werden in Standard-Reads gefiltert, defensive Pruefung trotzdem).
                c.render = (text, record) => (
                    <Space size={4} align="center">
                        <ManualAdjustIndicator record={record} refetch={refetch} />
                        {renderInvoiceTitle(record.title ?? undefined)}
                    </Space>
                );
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                // c.sorter = false;
                break;

            case "invoiceTitle":
                c.title = i18n.views.TableColumnTitles.invoiceTitle();
                c.fixed = false;
                c.render = (text, record) => (
                    <Space size={4} align="center">
                        <ManualAdjustIndicator record={record} refetch={refetch} />
                        {renderInvoiceTitle(record.title ?? undefined)}
                    </Space>
                );
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    // type: "search",
                    type: "autoComplete",
                    autoCompleteOptions: invoiceTitleAutoCompleteOptions,
                });
                // c.sorter = false;
                break;
            case "invoiceDate":
                c.align = "center";
                c.render = (text, record: TableType) => ({
                    displayName: "invoiceDate",
                    children: record.invoice?.updateDate ? renderDate(record.invoice?.updateDate) : <div>{"-"}</div>,
                });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "date",
                });
                c.sorter = false;
                break;
            case "noticeStaff":
                c.render = (text, record: TableType) => renderContinuousText(record.booking.noticeStaff, 200, undefined, options?.filter?.find((filter) => filter.column === c.dataIndex)?.values[0]);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "scheduledPaymentNote":
                c.render = (text, record: TableType) => {
                    const transferNote = transferPartialNoteForRate(record as TableType & {transferFromBookingNumber?: string | null; transferPaidAmount?: number | null; openAmount?: number | null; paymentAmount?: number | string | null}, currency.short as CurrencyShort);
                    // Übertrags-Hinweis zuerst: die Anzeige kappt bei 200 Zeichen, eine lange eigene Notiz darf ihn nicht verschlucken.
                    const note = [transferNote ?? "", record.note ?? ""].filter((part) => part.length > 0).join(" · ");

                    return renderContinuousText(note, 200, undefined, options?.filter?.find((filter) => filter.column === c.dataIndex)?.values[0]);
                };
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "nonDisclosureNotice":
                c.render = (text, record: TableType) => {
                    // sc-3040 follow-up — bei Sentinel-Filtern keinen Highlight-String
                    // an renderContinuousText durchreichen (würde "__NDN_…__" markieren).
                    const filterValue = options?.filter?.find((filter) => filter.column === c.dataIndex)?.values[0];
                    const highlight = filterValue === NDN_FILTER_PRESENT || filterValue === NDN_FILTER_ABSENT ? undefined : filterValue;

                    return renderContinuousText(record.booking.attendee?.booker?.nonDisclosureNotice ?? "", 200, undefined, highlight);
                };
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                // sc-3040 follow-up — bestehenden Such-Dropdown durch Drei-Modi-Variante
                // ersetzen (Text-Suche + "Alle mit / ohne Sperrvermerk"-Buttons).
                // Filter-Icon und filteredValue bleiben durch sortFilter gesetzt.
                c.filterDropdown = () => (
                    <FilterDropdownPresence
                        dataIndex={c.dataIndex as string}
                        options={options}
                        setOptions={setOptions}
                        setDropdownOpen={setDropdownOpen}
                        table="payment"
                        presentSentinel={NDN_FILTER_PRESENT}
                        absentSentinel={NDN_FILTER_ABSENT}
                        presentLabel="Alle mit Sperrvermerk anzeigen"
                        absentLabel="Alle ohne Sperrvermerk anzeigen"
                        placeholder="Sperrvermerk suchen"
                    />
                );
                c.sorter = false;
                break;

            case "paymentSource":
                c.align = "center";
                c.width = "13em";
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    const isMollie = record.paymentProvider === PaymentProvider.Mollie;

                    return (
                        <Tag color={isMollie ? "blue" : "default"} style={{margin: 0}}>
                            {isMollie ? i18n.containers.payments.PaymentsListColumns.providerMollie() : i18n.containers.payments.PaymentsListColumns.providerManual()}
                        </Tag>
                    );
                };
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {label: i18n.containers.payments.PaymentsListColumns.providerManual(), value: PaymentProvider.Manual},
                        {label: i18n.containers.payments.PaymentsListColumns.providerMollie(), value: PaymentProvider.Mollie},
                    ],
                });
                // Server-seitige Sortierung (AntD sendet sortColumn = dataIndex "paymentSource",
                // Backend ordnet ueber scheduledPayment.paymentProvider — siehe addSortersToQuery).
                c.sorter = true;
                break;

            case "mollieStatus":
                c.align = "center";
                c.width = 140;
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    if (record.paymentProvider !== PaymentProvider.Mollie) {
                        return <span>{"-"}</span>;
                    }

                    const mollieStatus = record.molliePaymentStatus;

                    // Phase 13 / DISP-06 + D-22 — "link_sent" Pseudo-State:
                    // rendert Blue-Tag + LinkOutlined wenn status === "open" UND
                    // linkExpiresAt > NOW(). Wir erkennen den Link-Sent-Zustand
                    // via `currentMolliePaymentAttempt.linkExpiresAt`. Die Feld-
                    // Verfuegbarkeit kommt aus Plan 13-03 (additiv).
                    const linkExpiresAt = (
                        record as TableType & {
                            currentMolliePaymentAttempt?: {linkExpiresAt?: Date | string | null} | null;
                        }
                    ).currentMolliePaymentAttempt?.linkExpiresAt;
                    const linkActive = mollieStatus === "open" && linkExpiresAt && moment(linkExpiresAt).isAfter(moment());
                    if (linkActive) {
                        const formattedExpiresAt = linkExpiresAt ? moment(linkExpiresAt).format("DD.MM.YY HH:mm") : "-";
                        return (
                            <Tooltip
                                title={i18n.containers.payments.OpenPaymentsList.linkSentBadgeTooltip({
                                    expiresAt: formattedExpiresAt,
                                })}
                            >
                                <Tag color="blue" icon={<LinkOutlined />} style={{margin: 0}} data-testid={`MollieLinkSentBadge-${record.id}`}>
                                    {i18n.containers.payments.OpenPaymentsList.linkSentBadge()}
                                </Tag>
                            </Tooltip>
                        );
                    }

                    // Phase 12 (LIFE-05, D-13/D-14/D-15): Wenn Methode geändert wurde,
                    // rendere Gold-Badge mit dynamischem Tooltip (Datum + User-Email aus
                    // dem methodChangedBy-FieldResolver). Keine Retry-/Chargeback-Buttons
                    // — method_changed ist ein Audit-Zustand, kein Fehler.
                    if (mollieStatus === "method_changed") {
                        const methodChangedAtDate = (record as TableType & {methodChangedAt?: Date | string | null}).methodChangedAt;
                        const methodChangedByEmail = (record as TableType & {methodChangedBy?: {email: string} | null}).methodChangedBy?.email ?? "-";
                        const formattedDate = methodChangedAtDate ? moment(methodChangedAtDate).format("DD.MM.YYYY HH:mm") : "-";
                        const tooltipTitle = i18n.containers.payments.PaymentsListColumns.methodChangedTooltip({
                            date: formattedDate,
                            user: methodChangedByEmail,
                        });
                        return (
                            <Tooltip title={tooltipTitle} placement="top">
                                <span data-testid="MolliePaymentMethodChangedBadge">
                                    <MollieStatusTag status={mollieStatus} />
                                </span>
                            </Tooltip>
                        );
                    }

                    // Phase 8 (D-11): Bei chargebacked zusaetzlich einen RetryButton zeigen,
                    // der die neue Manual-Retry-Mutation mit 10 €-Kostenwarnung und den
                    // 6 HIGH-3-Server-Guards aufruft. Non-retriable-Status wird anhand des
                    // Reason-Codes aus `mollieErrorMessage` bestimmt (best-effort) —
                    // der Server lehnt ungueltige Retries mit spezifischer i18n-Message
                    // ohnehin ab.
                    const isChargebacked = mollieStatus === "chargebacked";
                    const reasonCode = isChargebacked ? parseReasonCodeFromErrorMessage(record.mollieErrorMessage) : undefined;
                    const isNonRetriable = Boolean(reasonCode && PHASE_8_NON_RETRIABLE_CODES.has(reasonCode));

                    // Phase 14 / Plan 14-05 (D-13, D-15): Standalone-Refund-Button in
                    // DonePaymentsList-Kontext (nur bei paymentDone + Refund-Cap > 0).
                    // Sichtbarkeits-Check greift automatisch in MollieRefundButton selbst.
                    // Bug-Fix Mollie-Refund-Cap: Cap = (molliePaidAmount ?? paymentAmount) - refundedAmount.
                    // molliePaidAmount kann null/undefined sein → Fallback auf paymentAmount (= altes
                    // Verhalten `refundedAmount < paymentAmount`). Voll-erstattete Raten (Cap = 0)
                    // bauen keine refundRow → keine "Ghost-Row" im Panel.
                    const recordWithRefund = record as TableType & {refundedAmount?: number};
                    const refundedAmount = Number(recordWithRefund.refundedAmount || 0);
                    const refundCap = Number(record.molliePaidAmount ?? record.paymentAmount) - refundedAmount;
                    const canStandaloneRefund = Boolean(record.paymentDone) && refundCap > 0;
                    const refundRow: RefundPanelRow | null = canStandaloneRefund
                        ? {
                              scheduledPaymentId: record.id,
                              paymentAmount: Number(record.paymentAmount),
                              refundedAmount,
                              paymentDue: record.paymentDue,
                              paymentDone: record.paymentDone ?? null,
                              title: record.title ?? null,
                              paymentProvider: record.paymentProvider,
                              mollieMethod: record.mollieMethod ?? null,
                              molliePaidAmount: record.molliePaidAmount,
                          }
                        : null;

                    return (
                        <Space size={4}>
                            <MollieStatusTag status={mollieStatus} errorMessage={record.mollieErrorMessage} retryCount={record.mollieRetryCount} />
                            <RetryMolliePaymentButton
                                scheduledPaymentId={record.id}
                                mollieStatus={mollieStatus}
                                bookingCanceled={Boolean(record.booking?.canceled)}
                                refetch={refetch}
                                bundleGroup={computeSameDueMollieBundle(record, tableData)}
                                currency={currency.short}
                                recipientEmail={record.booking?.attendee?.booker?.email ?? undefined}
                            />
                            {isChargebacked && <ChargebackRetryButton spId={record.id} reasonCode={reasonCode} nonRetriable={isNonRetriable} />}
                            {refundRow && <MollieRefundButton scheduledPayment={refundRow} onRefunded={() => refetch?.()} size="small" />}
                        </Space>
                    );
                };
                break;

            case "mollieErrorMessage":
                c.align = "center";
                c.width = 200;
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    if (record.paymentProvider !== PaymentProvider.Mollie) {
                        return <span>{"-"}</span>;
                    }

                    const errorMessage = record.mollieErrorMessage;

                    if (!errorMessage) {
                        return <span>{"-"}</span>;
                    }

                    return renderContinuousText(errorMessage, 200);
                };
                break;

            case "mollieRetryCount":
                c.align = "center";
                c.width = 80;
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    if (record.paymentProvider !== PaymentProvider.Mollie) {
                        return <span>{"-"}</span>;
                    }

                    return <span>{record.mollieRetryCount ?? 0}</span>;
                };
                break;

            case "mollieLinkExpiresAt":
                // Phase 13 / DISP-06 + DISP-08 — Countdown-Spalte "Link gueltig bis".
                // Default-hidden via "nicht in allDefaultColumns"-Konvention (siehe
                // OpenPaymentsList.tsx); der Column-Picker aktiviert die Anzeige.
                c.align = "center";
                c.width = 140;
                c.title = i18n.containers.payments.PaymentsListColumns.columnTitleLinkExpires();
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    if (record.paymentProvider !== PaymentProvider.Mollie) {
                        return <span>{"-"}</span>;
                    }
                    const attempt = (
                        record as TableType & {
                            currentMolliePaymentAttempt?: {linkExpiresAt?: Date | string | null} | null;
                        }
                    ).currentMolliePaymentAttempt;
                    return <CountdownCell expiresAt={attempt?.linkExpiresAt} />;
                };
                c.sorter = false;
                break;

            case "mollieDispatchAction":
                // Phase 13 / DISP-01 + DISP-04 + DISP-08 — Row-Action "Zahlungslink senden"
                // + optionaler Konflikt-Badge. Liest featureMollie + conflictingSpIds
                // aus MolliePaymentLinkDispatchContext (siehe OpenPaymentsList-Provider).
                c.align = "center";
                c.width = 170;
                c.title = i18n.containers.payments.OpenPaymentsList.dispatchLink();
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    return <MolliePaymentLinkDispatchCell record={record} />;
                };
                c.sorter = false;
                break;

            case "refundStatus":
                // Phase 14 / Plan 14-05 (D-23) — Interim-Badge fuer Refund-State.
                // - providerRefundedAmount === 0      → null (kein Badge)
                // - providerRefundedAmount >= tatsaechlich bezahlt → Orange-Tag "Refund" (full)
                // - 0 < providerRefundedAmount < tatsaechlich bezahlt → Orange-Tag "Teil-Refund" + Tooltip
                c.align = "center";
                c.width = 120;
                c.title = i18n.containers.payments.PaymentsListColumns.refundStatusColumn();
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    const providerRefundedAmount = Number(
                        (record as TableType & {providerRefundedAmount?: number}).providerRefundedAmount || 0,
                    );
                    const refundBase = Number(record.molliePaidAmount ?? record.paymentAmount);
                    // #1069: Badge "Doppelzahlung" bewusst in DIESER Spalte, nicht in mollieStatus —
                    // eine doppelt bezahlte Rate hat nach Methodenwechsel oder CAMT-Eingang meist
                    // paymentProvider Manual, dort zeigt die Mollie-Status-Spalte nur "-".
                    const surplusCount = Number((record as TableType & {surplusMolliePaymentCount?: number | null}).surplusMolliePaymentCount || 0);
                    const surplusBadge = surplusCount > 0 ? <SurplusMolliePaymentBadge count={surplusCount} scheduledPaymentId={record.id} onResolved={refetch} /> : null;
                    if (providerRefundedAmount === 0) return surplusBadge || <span>{"-"}</span>;

                    const isFull = providerRefundedAmount >= refundBase;
                    const label = isFull ? i18n.containers.payments.PaymentsListColumns.refundBadgeFull() : i18n.containers.payments.PaymentsListColumns.refundBadgePartial();
                    const badge = (
                        <Tag color="orange" icon={<RollbackOutlined />} style={{margin: 0}} data-testid={`MollieRefundBadge-${record.id}`}>
                            {label}
                        </Tag>
                    );
                    const tooltipTitle = i18n.containers.payments.PaymentsListColumns.refundBadgeTooltip({
                        refunded: renderAmountHelper({amount: providerRefundedAmount, currency: currency.short as CurrencyShort, short: true}),
                        total: renderAmountHelper({amount: refundBase, currency: currency.short as CurrencyShort, short: true}),
                    });
                    const refundBadge = isFull ? badge : <Tooltip title={tooltipTitle}>{badge}</Tooltip>;

                    return surplusBadge ? (
                        <Space size={4}>
                            {refundBadge}
                            {surplusBadge}
                        </Space>
                    ) : (
                        refundBadge
                    );
                };
                c.sorter = false;
                break;

            case "paymentActionTimeline":
                // Phase 16 / AUDIT-02 — Row-Action-Icon HistoryOutlined öffnet
                // den Timeline-Drawer (LazyQuery). Callback via Context.
                c.align = "center";
                c.width = 60;
                c.title = "Historie";
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    return <PaymentActionTimelineCell record={record} />;
                };
                c.sorter = false;
                break;

            case "dunningLevel":
                c.title = i18n.views.TableColumnTitles.dunningLevel();
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => <DunningStatusCell status={dunningStatusOf(record)} />;
                c.sorter = false;
                break;

            case "tombstoneStatus":
                // B-223 Followup — Status-Spalte: "Aktiv"-Tag bei aktiven Posten,
                // "Gelöscht"-Tag (mit Audit-Tooltip) bei Tombstones (deletedAt!=null).
                // Restore-Aktion sitzt in der Action-Spalte (RenderActions).
                c.align = "center";
                c.width = 90;
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    return <TombstoneIndicator record={record} />;
                };
                c.sorter = false;
                break;

            default:
                c.fixed = false;
                c.render = (text) => renderUnknown(text);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "search",
                });
                break;
        }
        columns.push(c);
    });

    getColumnsToShow();

    return shownColumns;
};

// ACTION BUTTONS---------------------------------------------------------------------------

const SepaClearanceCheckbox = ({record, refetch}: {record: TableType; refetch?: () => void}) => {
    const {sepaClearance} = record;
    const [loading, setLoading] = useState<boolean>(false);

    const scheduledPaymentsRefetchQueries = (): BaseMutationOptions["refetchQueries"] => [
        "GetUnpaidScheduledPayments",
        {query: GetUnpaidScheduledPaymentsDocument, variables: {bookingId: record.booking.id}},
        "GetPaidScheduledPayments",
        {query: GetPaidScheduledPaymentsDocument, variables: {bookingId: record.booking.id}},
    ];

    const [setClearance] = useSetSepaClearanceMutation({
        refetchQueries: scheduledPaymentsRefetchQueries(),
        awaitRefetchQueries: true,
        onCompleted: () => {
            refetch?.();
        },
    });

    const updateSepaClearance = async (checked: boolean) => {
        try {
            setLoading(true);
            await setClearance({
                variables: {id: record.id, sepaClearance: checked},
            }).then(() => {
                setLoading(false);
                // refetch wird automatisch durch refetchQueries in der Mutation ausgelöst
            });
        } catch (_error) {
            // Fehler wird durch die UI (Spin-State) abgefangen
        }
    };

    const onChange = (e: CheckboxChangeEvent) => {
        updateSepaClearance(e.target.checked);
    };

    if (loading) {
        return <Spin size="small" />;
    }

    return <Checkbox checked={Boolean(sepaClearance)} onChange={onChange} />;
};

const renderActions = (
    record: TableType,
    editLink: string,
    detailsLink: string,
    action: "setAsPaid" | "setAsUnpaid",
    refetch?: () => void,
    editPaymentOpen?: boolean,
    setEditPaymentOpen?: (editPaymentOpen: boolean) => void,
    setRecordToEdit?: (record: TableType) => void,
) => {
    return (
        <RenderActions
            record={record}
            editLink={editLink}
            detailsLink={detailsLink}
            action={action}
            refetch={refetch}
            editPaymentOpen={editPaymentOpen}
            setEditPaymentOpen={(editPaymentOpen) => setEditPaymentOpen?.(editPaymentOpen)}
            setRecordToEdit={(record) => setRecordToEdit?.(record)}
        />
    );
};

type RenderActionsProps = {
    record: TableType;
    editLink: string;
    detailsLink: string;
    action: "setAsPaid" | "setAsUnpaid";
    refetch?: () => void;
    editPaymentOpen?: boolean;
    setEditPaymentOpen?: (editPaymentOpen: boolean) => void;
    setRecordToEdit?: (record: TableType) => void;
};

export const RenderActions: FC<RenderActionsProps> = ({record, editLink, detailsLink, action, refetch, editPaymentOpen, setEditPaymentOpen, setRecordToEdit}) => {
    const highestRole = useHighestRole();
    // Vor dem frühen Return der Tombstones, damit die Hook-Reihenfolge stabil bleibt.
    const withBankSuggestion = useBankSuggestionForMarkPaid();

    // As summary is only shown for certain roles, we need to check if the user has one of those roles -----------------
    const isBetaTester = useIsBetaTester();
    const summaryIsShown = [roles.Superuser, roles.SuperAdmin].includes(highestRole) || isBetaTester;

    // B-223 Followup — Tombstones zeigen statt der Standard-Action-ButtonGroup
    // einen einzelnen "Wiederherstellen"-Button. Edit/SetPaid/Delete sind auf
    // gelöschten Posten semantisch nicht sinnvoll: Edit würde Werte auf einem
    // tombstoned Record speichern, SetPaid wäre ein Status-Wechsel auf einem
    // Posten der nicht in Summen einfließt, und Delete ist bereits passiert.
    if ((record as TableType & {deletedAt?: Date | string | null}).deletedAt) {
        return <RestoreActionButton record={record} refetch={refetch} />;
    }

    const scheduledPaymentsRefetchQueries = (): BaseMutationOptions["refetchQueries"] => [
        "GetBookingById",
        {query: GetBookingByIdDocument, variables: {id: record.booking.id}},
        "GetUnpaidScheduledPayments",
        {query: GetUnpaidScheduledPaymentsDocument, variables: {bookingId: record.booking.id}},
        "GetPaidScheduledPayments",
        {query: GetPaidScheduledPaymentsDocument, variables: {bookingId: record.booking.id}},
        "GetBookingById",
        {query: GetBookingByIdDocument, variables: {id: record.booking.id}},
        "GetBookingByIdWithCourseLessonBookings",
        {query: GetBookingByIdWithCourseLessonBookingsDocument, variables: {id: record.booking.id}},
        // Mahnkarte: „Erneut senden“ ist gesperrt, sobald die Gebühr gelöscht ist
        "GetBookingDunnings",
    ];

    const [setScheduledPaymentAsPaid, {loading: setScheduledPaymentAsPaidLoading}] = useSetScheduledPaymentPaidMutation({
        refetchQueries: scheduledPaymentsRefetchQueries(),
        awaitRefetchQueries: true,
        onCompleted: (data) => {
            refetch?.();
        },
    });

    const setAsPaid = async (date: Moment | null, id: string) => {
        const hide = message.loading(i18n.containers.payments.OpenPaymentsList.operate(), 0);

        try {
            await setScheduledPaymentAsPaid({
                variables: {
                    id,
                    paymentDone: date,
                },
            });
            message.success(i18n.containers.payments.OpenPaymentsList.markedAsPaidFinished(), 0.7);
            // refetch wird automatisch durch refetchQueries in der Mutation ausgelöst
        } catch (_e) {
            message.error(i18n.containers.payments.OpenPaymentsList.errorOnMarkAsPaid(), 0.7);
        } finally {
            hide();
        }
    };

    const [setScheduledPaymentAsUnPaid, {loading: setScheduledPaymentAsUnPaidLoading}] = useSetScheduledPaymentUnpaidMutation({
        refetchQueries: scheduledPaymentsRefetchQueries(),
        awaitRefetchQueries: true,
        onCompleted: (data) => {
            refetch?.();
        },
    });

    const setAsUnPaid = async (id: string) => {
        const hide = message.loading(i18n.containers.payments.OpenPaymentsList.operate(), 0);

        try {
            await setScheduledPaymentAsUnPaid({
                variables: {
                    id,
                },
            });
            message.success(i18n.containers.payments.OpenPaymentsList.markedAsUnPaidFinished(), 0.7);
            // refetch wird automatisch durch refetchQueries in der Mutation ausgelöst
        } catch (_e) {
            message.error(i18n.containers.payments.OpenPaymentsList.errorOnMarkAsUnPaid(), 0.7);
        } finally {
            hide();
        }
    };

    // CHANGE PAYMENT DATE ---------------------------------------------------------------

    const [paymentDate, setPaymentDate] = useState<Moment | null>(null);

    React.useEffect(() => {
        if (!paymentDate) {
            setPaymentDate(record.paymentDone);
        }
    }, [paymentDate, record.paymentDone]);

    const [paymentDateModalOpen, setPaymentDateModalOpen] = useState<boolean>(false);

    const [setNewPaymentDoneDate, {loading: setNewPaymentDoneDateLoading}] = useSetNewPaymentDoneDateMutation({
        refetchQueries: scheduledPaymentsRefetchQueries(),
        awaitRefetchQueries: true,
    });

    const updatePaymentDate = async () => {
        if (!paymentDate) return;

        try {
            await setNewPaymentDoneDate({
                variables: {
                    id: record.id,
                    paymentDoneDate: paymentDate,
                },
            })
                .then(() => {
                    setPaymentDateModalOpen(false);
                })
                .then(() => {
                    message.success(i18n.containers.payments.OpenPaymentsList.paymentDateChanged(), 0.7);
                    // refetch wird automatisch durch refetchQueries in der Mutation ausgelöst
                });
        } catch (_error) {
            message.error(i18n.containers.payments.OpenPaymentsList.errorOnChangePaymentDate(), 0.7);
        }
    };

    const EditButton = () => {
        // Nach DSGVO-Löschantrag lehnt der Server jede Änderung an der Rate ab (Zahlungseingang und Löschen bleiben möglich).
        if (record.booking?.attendee?.erasedAt) {
            return (
                <Tooltip title={ERASED_ACTION_HINT}>
                    {/* span: deaktivierte Buttons lösen keine Maus-Events für den Tooltip aus */}
                    <span>
                        <Button size="small" disabled data-testid="EditPaymentButtonErased">
                            <EditOutlined />
                        </Button>
                    </span>
                </Tooltip>
            );
        }

        return (
            <>
                {/* <EditScheduledPaymentModal /> */}
                <Button
                    size="small"
                    style={{color: blue[5]}}
                    onClick={() => {
                        setEditPaymentOpen?.(true);
                        setRecordToEdit?.(record);
                    }}
                >
                    <EditOutlined />
                </Button>
            </>
        );
    };

    const [deletePayment, {loading: deletePaymentLoading}] = useDeleteScheduledPaymentMutation({
        refetchQueries: scheduledPaymentsRefetchQueries(),
        awaitRefetchQueries: true,
        onCompleted: () => {
            refetch?.();
        },
    });

    const confirmDeletePayment = async () => {
        try {
            await deletePayment({
                variables: {
                    id: record.id,
                },
            }).then(() => {
                message.success(i18n.containers.payments.OpenPaymentsList.deletePayment.paymentDeleted(), 0.7);
                // refetch wird automatisch durch refetchQueries in der Mutation ausgelöst
            });
        } catch (err) {
            // Server-Meldung anzeigen (analog RestoreActionButton): die Guards liefern
            // handlungsweisende Texte ("Bezahlte Raten können nicht gelöscht werden…",
            // "…offene Mollie-Zahlung, die nicht automatisch entwertet werden konnte…").
            const errorMessage = err instanceof Error ? err.message : String(err);

            message.error(i18n.containers.payments.OpenPaymentsList.deletePayment.errorOnDeletePayment({error: errorMessage}), 1.5);
        }
    };

    const [tooltipOpen, setTooltipOpen] = useState<boolean | undefined>(undefined);

    // #1517: Gehört die Rate zu einer gemahnten Buchung, sagt die Rückfrage das dazu (blockiert nicht)
    const deleteConfirmTitle = () => {
        const text = i18n.containers.payments.OpenPaymentsList.deletePayment.text();
        const dunning = dunningStatusOf(record);

        if (!dunning?.level) {
            return text;
        }
        const level = i18n.containers.invoice.BookingDunnings.levels[dunning.level]();
        const date = dunning.levelDate ? moment(dunning.levelDate).format("DD.MM.YYYY") : "";

        return (
            <div style={{maxWidth: 360}}>
                <div>{text}</div>
                <Typography.Text type="warning">{i18n.containers.invoice.BookingDunnings.rate.deleteText({level, date})}</Typography.Text>
            </div>
        );
    };

    const isDunningFee = [ScheduledPaymentType.DunningLevel1, ScheduledPaymentType.DunningLevel2, ScheduledPaymentType.DunningLevel3].includes(record.type);

    const deleteScheduledPaymentAction = () => {
        // Mahngebühr: Dialog fragt, ob nur die Gebühr erlassen oder die Mahnung zurückgenommen wird
        if (isDunningFee) {
            // Wie rateHasSettledMoney: teilbezahlt oder erstattet, dann lehnt der Server das Löschen ab
            const hasSettledMoney = (record.openAmount != null && record.openAmount > 0 && record.openAmount < record.paymentAmount) || Number(record.refundedAmount ?? 0) > 0;

            return (
                <DunningFeeDeleteButton
                    bookingId={record.booking.id}
                    scheduledPaymentId={record.id}
                    hasSettledMoney={hasSettledMoney}
                    deletePayment={confirmDeletePayment}
                    onWithdrawn={() => refetch?.()}
                />
            );
        }

        return (
            <Tooltip title={i18n.containers.payments.OpenPaymentsList.deletePayment.title()} placement="left" mouseEnterDelay={0.75} destroyTooltipOnHide open={tooltipOpen}>
                <Popconfirm
                    title={deleteConfirmTitle()}
                    onConfirm={confirmDeletePayment}
                    okText={i18n.containers.payments.OpenPaymentsList.deletePayment.delete()}
                    okButtonProps={{
                        danger: true,
                        style: {background: red[3], borderColor: red[3]},
                    }}
                    cancelText={i18n.containers.payments.OpenPaymentsList.deletePayment.cancel()}
                    placement="left"
                    onOpenChange={(open) => {
                        if (open) {
                            setTooltipOpen(false);
                        } else setTooltipOpen(undefined);
                    }}
                >
                    <Button size="small" type="link" style={{color: red[3]}}>
                        <DeleteOutlined />
                    </Button>
                </Popconfirm>
            </Tooltip>
        );
    };

    /** Prüft ob ein Mollie-Payment vorliegt und zeigt ggf. einen Warndialog */
    const handleSetAsPaidWithMollieCheck = (date: Moment | null) => {
        if (!record.id) return;
        const recordId = record.id;

        if (record.paymentProvider === PaymentProvider.Mollie) {
            Modal.confirm({
                title: i18n.containers.payments.PaymentsListColumns.mollieWarning.singleTitle(),
                content: i18n.containers.payments.PaymentsListColumns.mollieWarning.singleContent(),
                okText: i18n.containers.payments.PaymentsListColumns.mollieWarning.okText(),
                cancelText: i18n.containers.payments.PaymentsListColumns.mollieWarning.cancelText(),
                onOk: () => setAsPaid(date, record.id),
            });
            return;
        }

        // Liegt der Zahlungseingang schon im Bankabgleich, bietet der Dialog an, ihn mit Bankreferenz zu übernehmen.
        void withBankSuggestion(
            recordId,
            () => void setAsPaid(date, recordId),
            () => refetch?.(),
        );
    };

    if (action === "setAsPaid") {
        return (
            <ButtonGroup>
                <EditButton />
                <CalendarPopover
                    title={i18n.containers.payments.OpenPaymentsList.paymentDate()}
                    buttonLeftText={i18n.containers.payments.OpenPaymentsList.changePaymentPopover.cancel()}
                    buttonRightClick={(date) => handleSetAsPaidWithMollieCheck(date)}
                    buttonRightText={i18n.containers.payments.OpenPaymentsList.accept()}
                >
                    <Button type="primary" size="small">
                        {i18n.containers.payments.OpenPaymentsList.markAsPaid()}
                    </Button>
                </CalendarPopover>
                {deleteScheduledPaymentAction()}
            </ButtonGroup>
        );
    }

    const donePaymentActions = () => {
        // Mollie-Lock (Plan 2026-05-20): Eine ueber Mollie bezahlte Rate darf nicht
        // manuell auf unbezahlt gesetzt werden (Status-Drift). Reversal laeuft ueber Refund.
        const isMolliePaid = record.paymentProvider === PaymentProvider.Mollie && record.paymentDone !== null;
        return (
            <Button.Group>
                <EditButton />
                <Tooltip title={isMolliePaid ? i18n.containers.payments.OpenPaymentsList.mollieUnpaidLockedTooltip() : undefined}>
                    {/* AntD v4: Tooltip auf disabled Button braucht einen span-Wrapper, sonst kein Hover-Event. */}
                    <span>
                        <Button size="small" onClick={() => record.id && setAsUnPaid(record.id)} disabled={record.booking.canceled !== null || isMolliePaid}>
                            {i18n.containers.payments.OpenPaymentsList.markAsUnPaid()}
                        </Button>
                    </span>
                </Tooltip>
                {isMolliePaid ? (
                    // Mollie-Lock (Plan 2026-05-20 / Review-Fix): Das Bezahldatum-aendern-Popover
                    // (Dreipunkt-Button) ist ein weiterer Bypass fuer den Zahldatum-Lock. Bei
                    // Mollie-bezahlten Posten wird der Trigger disabled (kein Popover) und mit
                    // einem erklaerenden Tooltip versehen. AntD v4: Tooltip auf disabled Button
                    // braucht einen span-Wrapper, sonst kein Hover-Event.
                    <Tooltip title={i18n.containers.payments.OpenPaymentsList.mollieDateLockedTooltip()}>
                        <span>
                            <Button icon={<MoreOutlined />} size="small" disabled />
                        </span>
                    </Tooltip>
                ) : (
                    <Popover
                        title={i18n.containers.payments.OpenPaymentsList.changePaymentPopover.title()}
                        open={paymentDateModalOpen}
                        placement="left"
                        trigger="click"
                        overlayClassName={css.root}
                        content={
                            <Space direction="vertical">
                                <DateInput
                                    onChange={(date) => setPaymentDate(date)}
                                    style={{width: "100%"}}
                                    value={paymentDate ? moment(paymentDate) : undefined}
                                    displayFormat="DD.MM.YYYY"
                                    disabled={setNewPaymentDoneDateLoading}
                                />
                                <Space>
                                    <Button danger onClick={() => setPaymentDateModalOpen(false)}>
                                        {i18n.containers.payments.OpenPaymentsList.changePaymentPopover.cancel()}
                                    </Button>
                                    <Button type="primary" onClick={updatePaymentDate}>
                                        {i18n.containers.payments.OpenPaymentsList.changePaymentPopover.save()}
                                    </Button>
                                </Space>
                            </Space>
                        }
                    >
                        <Button icon={<MoreOutlined />} size="small" onClick={() => setPaymentDateModalOpen(!paymentDateModalOpen)} />
                    </Popover>
                )}
            </Button.Group>
        );
    };

    return donePaymentActions();
};

// ── Mollie Status Components ─────────────────────────────────────────────────

/**
 * Mapping von Mollie-Status zu Ant Design Tag-Farben und deutschem Label
 */
export const getMollieStatusDisplay = (status: string | undefined | null): {color: string; label: string; icon?: React.ReactNode} => {
    switch (status) {
        case "open":
            return {color: "blue", label: i18n.containers.payments.PaymentsListColumns.mollieStatus.open()};
        case "pending":
            return {color: "orange", label: i18n.containers.payments.PaymentsListColumns.mollieStatus.pending()};
        case "paid":
            return {color: "green", label: i18n.containers.payments.PaymentsListColumns.mollieStatus.paid()};
        case "failed":
            return {color: "red", label: i18n.containers.payments.PaymentsListColumns.mollieStatus.failed()};
        case "expired":
            return {color: "default", label: i18n.containers.payments.PaymentsListColumns.mollieStatus.expired()};
        case "canceled":
            return {color: "default", label: i18n.containers.payments.PaymentsListColumns.mollieStatus.canceled()};
        case "creation_failed":
            return {color: "red", label: i18n.containers.payments.PaymentsListColumns.mollieStatus.creationFailed()};
        case "chargebacked":
            return {color: "volcano", label: i18n.containers.payments.PaymentsListColumns.mollieStatus.chargebacked()};
        // Phase 12 (LIFE-05, D-13/D-14): Methode geändert — Gold-Badge mit WarningOutlined.
        // Dynamischer Tooltip (methodChangedAt + methodChangedBy.email) wird im Column-Render
        // gesetzt (Zeile ~599-637); dieser Branch liefert nur das Basis-Display (Farbe + Label + Icon).
        case "method_changed":
            return {
                color: "gold",
                label: i18n.containers.payments.PaymentsListColumns.mollieStatus.methodChanged(),
                icon: <WarningOutlined style={{marginRight: 4}} />,
            };
        default:
            return {color: "default", label: status || "-"};
    }
};

/**
 * Wandelt die rohe Mollie-Fehlermeldung in einen benutzerfreundlichen Text um.
 * Versucht aus dem JSON-Body das "detail"-Feld zu extrahieren.
 */
const parseMollieErrorMessage = (rawMessage: string | null | undefined): string | null => {
    if (!rawMessage) return null;

    // Versuche JSON aus der Fehlermeldung zu extrahieren (Format: "Mollie create payment API error: 422 {...}")
    const jsonMatch = rawMessage.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
        try {
            const parsed = JSON.parse(jsonMatch[0]);
            if (parsed.detail) {
                return parsed.detail;
            }
            if (parsed.title) {
                return parsed.title;
            }
        } catch (_e) {
            // JSON-Parsing fehlgeschlagen, Fallback
        }
    }

    // Bekannte Fehlermuster bereinigen
    const cleaned = rawMessage.replace(/^Mollie create payment API error:\s*\d+\s*/, "").trim();

    // Falls nach Bereinigung nur JSON übrig ist, generischen Text zeigen
    if (cleaned.startsWith("{")) {
        return i18n.containers.payments.PaymentsListColumns.mollieTooltip.creationFailedAction();
    }

    return cleaned || null;
};

export const MollieStatusTag: FC<{
    status: string | undefined | null;
    errorMessage?: string | null;
    retryCount?: number | null;
}> = ({status, errorMessage, retryCount}) => {
    if (!status) {
        return <span>{"-"}</span>;
    }

    const {color, label, icon} = getMollieStatusDisplay(status);

    const isErrorStatus = status === "creation_failed" || status === "failed" || status === "expired" || status === "canceled";

    if (!isErrorStatus) {
        return (
            <Tag color={color} style={{margin: 0}}>
                {icon}
                {label}
            </Tag>
        );
    }

    // Tooltip-Inhalt basierend auf Status
    let tooltipTitle = "";
    let tooltipAction = "";

    switch (status) {
        case "creation_failed":
            tooltipTitle = i18n.containers.payments.PaymentsListColumns.mollieTooltip.creationFailedTitle();
            tooltipAction = i18n.containers.payments.PaymentsListColumns.mollieTooltip.creationFailedAction();
            break;
        case "failed":
            tooltipAction = i18n.containers.payments.PaymentsListColumns.mollieTooltip.failedAction();
            break;
        case "expired":
            tooltipAction = i18n.containers.payments.PaymentsListColumns.mollieTooltip.expiredAction();
            break;
        case "canceled":
            tooltipAction = i18n.containers.payments.PaymentsListColumns.mollieTooltip.canceledAction();
            break;
    }

    // Technische Fehlermeldung in benutzerfreundlichen Text umwandeln
    const parsedError = parseMollieErrorMessage(errorMessage);

    const tooltipContent = (
        <div style={{maxWidth: 320}}>
            {tooltipTitle && <div style={{fontWeight: 600, marginBottom: 4}}>{tooltipTitle}</div>}
            {parsedError && <div style={{marginBottom: 4, fontSize: 12, color: "rgba(255,255,255,0.85)"}}>{parsedError}</div>}
            {retryCount != null && retryCount > 0 && <div style={{marginBottom: 4}}>{i18n.containers.payments.PaymentsListColumns.mollieTooltip.retryInfo({count: String(retryCount)})}</div>}
            <div>{tooltipAction}</div>
        </div>
    );

    return (
        <Tooltip title={tooltipContent} placement="top">
            <Tag color={color} style={{margin: 0, cursor: "help"}}>
                {label}
            </Tag>
        </Tooltip>
    );
};

/** Mollie-Status-Werte, bei denen ein Retry moeglich ist */
const RETRYABLE_MOLLIE_STATUSES = ["expired", "failed", "canceled", "creation_failed"];

/**
 * WP0b — Berechnet die "zusammen fällige" offene Mollie-Gruppe für den Retry
 * (analog Server-Helper loadSameDueMollieBundle): gleiche Rechnung, gleicher
 * Fälligkeitstag wie der angeklickte Posten, offen, Mollie, KEIN Discount.
 * Der angeklickte Posten steht als Anchor an erster Stelle. Enthält die Gruppe
 * weniger als 2 Posten, verhält sich der Retry wie bisher (Einzel-Bestätigung).
 */
export const computeSameDueMollieBundle = (
    record: TableType,
    allRows: Array<TableType> | undefined,
): TableType[] => {
    const invoiceId = record.invoice?.id;
    if (!invoiceId || !allRows) {
        return [record];
    }
    // Parität zum Server (mollie.invoiceBundle.ts dueDayKey): BEIDE gruppieren nach dem
    // LOKALEN Kalendertag (moment(...).format hier, lokale Date-Getter dort). Das ist
    // bewusst konsistent — ein UTC-basierter Schlüssel hier würde sonst an der
    // Tagesgrenze anders bündeln als der Server (vgl. SEPA-UTC-Offset-Bug-Historie).
    const dayKey = (d: TableType["paymentDue"]): string => (d ? moment(d).format("YYYY-MM-DD") : "");
    const anchorKey = dayKey(record.paymentDue);
    const group = allRows.filter(
        (row) =>
            row.invoice?.id === invoiceId &&
            !row.paymentDone &&
            row.paymentProvider === PaymentProvider.Mollie &&
            row.type !== ScheduledPaymentType.Discount &&
            dayKey(row.paymentDue) === anchorKey,
    );
    if (!group.some((r) => r.id === record.id)) {
        return [record];
    }
    // Anchor (angeklickter Posten) zuerst, danach die übrigen Gruppen-Posten.
    return [record, ...group.filter((r) => r.id !== record.id)];
};

type RetryMolliePaymentButtonProps = {
    scheduledPaymentId: string;
    mollieStatus: string | undefined | null;
    bookingCanceled?: boolean;
    refetch?: () => void;
    // WP0b: Bei einer Bündelung (>= 2 zusammen fällige offene Posten) zeigt der Retry
    // ein Modal mit Posten-Tabelle + Gesamtsumme; bei einem einzelnen Posten bleibt
    // die bisherige Popconfirm.
    bundleGroup?: TableType[];
    currency?: string;
    recipientEmail?: string;
};

export const RetryMolliePaymentButton: FC<RetryMolliePaymentButtonProps> = ({
    scheduledPaymentId,
    mollieStatus,
    bookingCanceled,
    refetch,
    bundleGroup,
    currency,
    recipientEmail,
}) => {
    const isRetryable = Boolean(mollieStatus && RETRYABLE_MOLLIE_STATUSES.indexOf(mollieStatus) !== -1);

    const [retryMolliePayment, {loading}] = useMutation(RETRY_MOLLIE_PAYMENT, {
        variables: {scheduledPaymentId},
        onCompleted: () => {
            message.success(i18n.containers.payments.PaymentsListColumns.retryPaymentLink.success(), 3);
            refetch?.();
        },
        onError: (error) => {
            message.error(i18n.containers.payments.PaymentsListColumns.retryPaymentLink.error() + error.message, 5);
        },
    });

    if (!isRetryable || bookingCanceled) {
        return null;
    }

    // WICHTIG: KEIN Alias auf i18n.containers...retryPaymentLink — der i18n-Proxy ist
    // zustandsbehaftet (Single-Instance, pfadabhängig); ein zwischengespeicherter
    // Teilpfad rendert nur beim ERSTEN Aufruf korrekt, danach rohe Fallback-Strings
    // (dokumentiertes Projekt-Gotcha). Daher an jeder Stelle den vollen Pfad inline.
    const group = bundleGroup ?? [];
    const isBundle = group.length >= 2;
    const cur = currency ?? "EUR";

    const retryButton = (
        <Tooltip title={i18n.containers.payments.PaymentsListColumns.retryPaymentLink.tooltip()}>
            <Button
                size="small"
                type={mollieStatus === "creation_failed" ? "primary" : "link"}
                icon={<ReloadOutlined />}
                loading={loading}
                style={mollieStatus === "creation_failed" ? {padding: "0 4px"} : {color: blue[5], padding: 0}}
                onClick={isBundle ? () => openBundleRetryModal() : undefined}
            />
        </Tooltip>
    );

    function openBundleRetryModal(): void {
        const total = group.reduce((sum, sp) => sum + Number(sp.paymentAmount || 0), 0);
        Modal.confirm({
            title: i18n.containers.payments.PaymentsListColumns.retryPaymentLink.bundleModalTitle(),
            okText: i18n.containers.payments.PaymentsListColumns.retryPaymentLink.bundleModalOk(),
            cancelText: i18n.containers.payments.PaymentsListColumns.retryPaymentLink.cancelText(),
            width: 640,
            content: (
                <div>
                    <p>{i18n.containers.payments.PaymentsListColumns.retryPaymentLink.bundleModalIntro()}</p>
                    <Table
                        dataSource={group.map((sp) => ({
                            key: sp.id,
                            paymentDue: sp.paymentDue ? moment(sp.paymentDue).format("DD.MM.YY") : "",
                            description: sp.title ?? "",
                            paymentAmount: Number(sp.paymentAmount).toFixed(2).replace(".", ",") + " " + cur,
                        }))}
                        columns={[
                            {title: i18n.containers.payments.PaymentsListColumns.retryPaymentLink.bundleModalColumnDueDate(), dataIndex: "paymentDue", key: "paymentDue"},
                            {title: i18n.containers.payments.PaymentsListColumns.retryPaymentLink.bundleModalColumnTitle(), dataIndex: "description", key: "description"},
                            {title: i18n.containers.payments.PaymentsListColumns.retryPaymentLink.bundleModalColumnAmount(), dataIndex: "paymentAmount", key: "paymentAmount"},
                        ]}
                        pagination={false}
                        size="small"
                    />
                    <p style={{marginTop: 16, fontWeight: 600}}>
                        {i18n.containers.payments.PaymentsListColumns.retryPaymentLink.bundleModalTotal({amount: total.toFixed(2).replace(".", ","), currency: cur})}
                    </p>
                    {recipientEmail ? <p>{i18n.containers.payments.PaymentsListColumns.retryPaymentLink.bundleModalRecipient({email: recipientEmail})}</p> : null}
                </div>
            ),
            onOk: () => retryMolliePayment(),
        });
    }

    if (isBundle) {
        // Klick öffnet das Bundle-Modal (onClick am Button); keine Popconfirm.
        return retryButton;
    }

    return (
        <Popconfirm
            title={i18n.containers.payments.PaymentsListColumns.retryPaymentLink.confirmMessage()}
            onConfirm={() => retryMolliePayment()}
            okText={i18n.containers.payments.PaymentsListColumns.retryPaymentLink.confirmOk()}
            cancelText={i18n.containers.payments.PaymentsListColumns.retryPaymentLink.cancelText()}
            placement="left"
        >
            {retryButton}
        </Popconfirm>
    );
};

// ── Phase 13 Dispatch-Row-Action-Components ───────────────────────────────────

/**
 * Phase 13 / DISP-01 + DISP-04 — Row-Action "Zahlungslink senden" fuer eine
 * offene Mollie-Standard-ScheduledPayment in OpenPaymentsList.
 *
 * Sichtbarkeit-Gates (RESEARCH Anti-Pattern "Row-Action unconditional"):
 *   featureMollie && paymentProvider === Mollie && !paymentDone && type === Standard
 *
 * Rate-Limit-Handling (D-18): bei ForbiddenError 60s Button-Disable + Toast.
 */
export type MolliePaymentLinkDispatchButtonProps = {
    record: TableType;
    featureMollie: boolean;
    refetch?: () => void;
};

export const MolliePaymentLinkDispatchButton: FC<MolliePaymentLinkDispatchButtonProps> = ({record, featureMollie, refetch}) => {
    const [rateLimitDisabledUntil, setRateLimitDisabledUntil] = useState<number | null>(null);
    // Gleiche Weiche wie der Server (sendMolliePaymentLinkFromAdmin): Überweisungsraten
    // bekommen ihre Zahlungsdaten erneut, keinen Zahlungslink.
    const isBanktransfer = record.mollieMethod === "banktransfer";
    const [sendDispatch, {loading}] = useMutation(SEND_MOLLIE_PAYMENT_LINK_FROM_ADMIN, {
        onCompleted: (data) => {
            const payload = data?.sendMolliePaymentLinkFromAdmin;
            if (!payload) return;
            const email = payload.bookerEmail ?? "";

            if (isBanktransfer) {
                // #1110: Der Server verschickt hier die Überweisungsdaten, keinen Link. Mollies
                // expiresAt ist der Tag NACH dem letzten gültigen Tag, daher derselbe Helfer wie
                // in Mail und Buchungsdetail.
                const validUntil = payload.linkExpiresAt ? formatMollieBanktransferLastValidDay(payload.linkExpiresAt, "DD.MM.YY") : "-";
                message.success(i18n.containers.payments.OpenPaymentsList.dispatchBanktransferSuccessToast({email, validUntil}), 3);
            } else {
                const expiresAtFmt = payload.linkExpiresAt ? moment(payload.linkExpiresAt).format("DD.MM.YY HH:mm") : "-";
                message.success(i18n.containers.payments.OpenPaymentsList.dispatchSuccessToast({email, expiresAt: expiresAtFmt}), 3);
            }
            refetch?.();
        },
        onError: (error) => {
            // Klassifikation via geteiltem Helper: liest extensions.code aus dem
            // ApolloError. MOLLIE_SCOPE_MISSING (Reconnect nötig) hat Vorrang vor
            // FORBIDDEN/Rate-Limit (server/mollie/mollieRateLimit.ts).
            const kind = classifyMollieDispatchError(error);
            if (kind === "scopeMissing") {
                // paymentlinks-7days: Der Server signalisiert, dass der OAuth-Grant
                // den Scope `payment-links.write` nicht erteilt hat. Statt des
                // generischen "später erneut versuchen"-Toasts zeigen wir den
                // handlungsweisenden Reconnect-Hinweis (Einstellungen → Mollie).
                notification.warning({
                    message: i18n.containers.common.MollieReconnect.dispatchScopeMissingTitle(),
                    description: i18n.containers.common.MollieReconnect.dispatchScopeMissingDescription(),
                    duration: 0,
                });
            } else if (kind === "rateLimit") {
                setRateLimitDisabledUntil(Date.now() + 60 * 1000);
                const rateLimitArgs = {retryInMinutes: "60"};
                message.error(
                    isBanktransfer
                        ? i18n.containers.payments.OpenPaymentsList.dispatchBanktransferRateLimitToast(rateLimitArgs)
                        : i18n.containers.payments.OpenPaymentsList.dispatchRateLimitToast(rateLimitArgs),
                    5,
                );
            } else {
                // Sperre nach DSGVO-Löschantrag: die Server-Meldung nennt den Grund, „später erneut versuchen“ wäre falsch.
                message.error(
                    erasedRecordMessage(error) ?? (isBanktransfer ? i18n.containers.payments.OpenPaymentsList.dispatchBanktransferGenericErrorToast() : i18n.containers.payments.OpenPaymentsList.dispatchGenericErrorToast()),
                    5,
                );
            }
        },
    });

    // Strikte Sichtbarkeits-Gates (Pitfall "Row-Action unconditional").
    // Alle einziehbaren Posten-Typen (Kursgebühr, Zusatzposten, Gebühren) — nur
    // Rabatt-Gutschriften nicht; dieselbe Regel wie der Server-Guard in
    // createBundleDispatchPaymentLink. Vorher nur Standard: eine offene
    // Anmeldegebühr (Zusatzposten) hatte keinen Knopf (Krieler Welle 07.09.2026).
    const isVisible = featureMollie && record.paymentProvider === PaymentProvider.Mollie && !record.paymentDone && record.type !== ScheduledPaymentType.Discount;

    if (!isVisible) {
        return null;
    }

    const isRateLimited = rateLimitDisabledUntil !== null && Date.now() < rateLimitDisabledUntil;
    const isDisabled = loading || isRateLimited;

    let tooltipText = isBanktransfer ? i18n.containers.payments.OpenPaymentsList.dispatchBanktransferTooltip() : i18n.containers.payments.OpenPaymentsList.dispatchLinkTooltip();

    if (isRateLimited) {
        tooltipText = i18n.containers.payments.OpenPaymentsList.dispatchLinkDisabledTooltip();
    }

    return (
        <Tooltip title={tooltipText}>
            <Button
                size="small"
                icon={<LinkOutlined />}
                disabled={isDisabled}
                loading={loading}
                onClick={() =>
                    sendDispatch({
                        variables: {scheduledPaymentId: record.id, locale: "de"},
                    })
                }
                data-testid={`mollie-dispatch-button-${record.id}`}
                style={{padding: "0 6px"}}
            >
                {isBanktransfer ? i18n.containers.payments.OpenPaymentsList.dispatchBanktransfer() : i18n.containers.payments.OpenPaymentsList.dispatchLink()}
            </Button>
        </Tooltip>
    );
};

/**
 * Phase 13 / DISP-08 — Konflikt-Badge "In aktivem Buendel enthalten".
 *
 * Wird zusaetzlich zum mollieStatus-Badge gerendert, wenn die SP-ID im
 * Ergebnis von `DetectBundleConflictsFromAdmin` enthalten ist. Der Caller
 * (OpenPaymentsList) fetcht die Konflikt-Liste einmalig pro Buchung und
 * reicht das Set an die Zeile durch.
 */
export type MollieBundleConflictBadgeProps = {
    conflicting: boolean;
};

export const MollieBundleConflictBadge: FC<MollieBundleConflictBadgeProps> = ({conflicting}) => {
    if (!conflicting) {
        return null;
    }
    return (
        <Tooltip title={i18n.containers.payments.OpenPaymentsList.conflictBadgeTooltip()}>
            <Tag color="orange" icon={<WarningOutlined />} style={{margin: 0, marginLeft: 4}} data-testid="MollieBundleConflictBadge">
                {i18n.containers.payments.OpenPaymentsList.conflictBadge()}
            </Tag>
        </Tooltip>
    );
};

/**
 * Phase 13 / Column-Cell — kombiniert Dispatch-Button + Konflikt-Badge aus
 * dem Context heraus. Rendert nichts, wenn featureMollie aus ist oder die
 * Sichtbarkeits-Gates der Button-Komponente nicht erfuellt sind.
 */
const MolliePaymentLinkDispatchCell: FC<{record: TableType}> = ({record}) => {
    const {featureMollie, conflictingSpIds, refetch} = useContext(MolliePaymentLinkDispatchContext);
    const isConflict = conflictingSpIds.has(record.id);
    return (
        <Space size={4}>
            <MolliePaymentLinkDispatchButton record={record} featureMollie={featureMollie} refetch={refetch} />
            <MollieBundleConflictBadge conflicting={isConflict} />
        </Space>
    );
};

/**
 * Phase 16 / AUDIT-02 — Cell-Komponente für die Timeline-Drawer-Icon-Spalte.
 *
 * Rendert das HistoryOutlined-Icon + onClick → Context.onOpenTimeline(spId).
 * Wenn der Context keinen Callback gesetzt hat (z.B. in DonePaymentsList, wo
 * der Drawer nicht gerendert wird), rendert die Zelle nichts — das verhindert
 * tote Icons mit no-op-Klick.
 *
 * `e.stopPropagation` verhindert, dass der Row-DoubleClick (EditPayment-Modal)
 * mit dem Icon-Click kollidiert.
 */
const PaymentActionTimelineCell: FC<{record: TableType}> = ({record}) => {
    const {onOpenTimeline} = useContext(PaymentActionTimelineContext);
    if (!onOpenTimeline) return null;
    return (
        <Tooltip title="Zahlungs-Historie anzeigen">
            <HistoryOutlined
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenTimeline(record.id);
                }}
                style={{cursor: "pointer", fontSize: 16, color: blue[5]}}
                data-testid={"PaymentActionTimelineOpener-" + record.id}
            />
        </Tooltip>
    );
};

/**
 * B-223 Wave 7 — Indikator + Popover fuer manuell geschuetzte ScheduledPayments.
 *
 * Discriminator wie der Server (isIndependentBookingPost, ohne carriedOverAt):
 *   - manuallyAddedAt != null oder Typ AdditionalItem → manuell hinzugefügter Posten.
 *     Gehört nicht zum Kurs-Zahlplan und bleibt bei jedem Recalc erhalten, der Pin
 *     hat dort keine Wirkung → kein Reset-Button.
 *   - sonst → manuell angepasste Rate; ein Reset führt beim nächsten Recalc zur
 *     Neuberechnung von Betrag/Fälligkeit.
 * Früher entschied der Titel. Seit Kursraten eine eigene Bezeichnung haben dürfen
 * (#1247), galt eine umbenannte Kursgebühr dadurch fälschlich als hinzugefügt.
 *
 * Edge cases:
 *   - manuallyAdjustedAt null/undefined → kein Render
 *   - deletedAt != null (Tombstone)     → defensiv kein Render (sollte in
 *     Standard-Reads eh nicht erscheinen)
 *   - paymentDone != null (bezahlt)     → Reset-Button disabled mit Tooltip
 *   - manuallyAdjustedBy null (User
 *     nicht aufloesbar)                 → Audit-Zeile ohne User-Email
 *
 * UX:
 *   - Pin-Icon (PushpinFilled) klein (14px), Gold (#faad14) — visuell konsistent
 *     mit dem methodChangedTooltip-Pattern, Pin-Metapher fuer "angeheftet".
 *   - Popover statt Tooltip, weil Action-Button eingebettet werden muss.
 *   - Trigger="click" — bewusst hover-frei, damit der User nicht aus Versehen
 *     einen Action-Button erwischt.
 */
type ManualAdjustRecord = TableType & {
    manuallyAdjustedAt?: Maybe<Date | string>;
    manuallyAddedAt?: Maybe<Date | string>;
    manuallyAdjustedBy?: Maybe<{id: string; email: string}>;
    deletedAt?: Maybe<Date | string>;
};

export const ManualAdjustIndicator: FC<{record: TableType; refetch?: () => void}> = ({record, refetch}) => {
    const adjustRecord = record as ManualAdjustRecord;
    const manuallyAdjustedAt = adjustRecord.manuallyAdjustedAt;
    const isTombstone = Boolean(adjustRecord.deletedAt);
    const [open, setOpen] = useState(false);

    const refetchQueries = (): BaseMutationOptions["refetchQueries"] => [
        // Operation-Namen sind lowercase (siehe scheduledPayments.graphql.ts:135/400) —
        // Apollo matched refetchQueries-Strings exakt zum Operation-Namen.
        "getUnpaidScheduledPayments",
        "getPaidScheduledPayments",
        // Settlement-Details-Seite (Abrechnung) nutzt diese Query — ohne Refetch
        // bliebe der Pin im UI stehen, obwohl Server manuallyAdjustedAt geleert hat.
        "GetBookingByIdWithCourseLessonBookings",
        {query: GetBookingByIdWithCourseLessonBookingsDocument, variables: {id: record.booking.id}},
    ];

    const [clearManuallyAdjusted, {loading}] = useClearManuallyAdjustedMutation({
        refetchQueries: refetchQueries(),
        awaitRefetchQueries: true,
    });

    if (!manuallyAdjustedAt || isTombstone) {
        return null;
    }

    const isAdded = Boolean(adjustRecord.manuallyAddedAt) || record.type === ScheduledPaymentType.AdditionalItem;
    const isPaid = Boolean(record.paymentDone);

    const adjustedDateFormatted = moment(manuallyAdjustedAt).format("DD.MM.YYYY HH:mm");
    const userEmail = adjustRecord.manuallyAdjustedBy?.email ?? null;

    const auditLine = userEmail
        ? i18n.containers.payments.PaymentsListColumns.manualAdjust.auditWithUser({
              date: adjustedDateFormatted,
              user: userEmail,
          })
        : i18n.containers.payments.PaymentsListColumns.manualAdjust.auditWithoutUser({
              date: adjustedDateFormatted,
          });

    const header = isAdded ? i18n.containers.payments.PaymentsListColumns.manualAdjust.headerAdded() : i18n.containers.payments.PaymentsListColumns.manualAdjust.headerEdited();

    const explanation = isAdded ? i18n.containers.payments.PaymentsListColumns.manualAdjust.explanationAdded() : i18n.containers.payments.PaymentsListColumns.manualAdjust.explanationEdited();

    const handleReset = async () => {
        try {
            await clearManuallyAdjusted({variables: {id: record.id}});
            message.success(i18n.containers.payments.PaymentsListColumns.manualAdjust.successMessage(), 0.7);
            setOpen(false);
            // Settlement-Details-Tabelle (OpenPaymentsList) refresht ueber den
            // timeStamp-getriebenen handleRefetch — sonst bleibt Pin/Wert stehen.
            refetch?.();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            message.error(i18n.containers.payments.PaymentsListColumns.manualAdjust.errorMessage({error: errorMessage}), 1.5);
        }
    };

    const popoverContent = (
        <div style={{maxWidth: 360}}>
            <div style={{fontWeight: 600, marginBottom: 6}}>{header}</div>
            <div style={{marginBottom: 8, color: "rgba(0,0,0,0.65)"}}>{explanation}</div>
            <div style={{marginBottom: 12, fontSize: 12, color: "rgba(0,0,0,0.45)"}}>{auditLine}</div>
            {isAdded ? null : isPaid ? (
                <Tooltip title={i18n.containers.payments.PaymentsListColumns.manualAdjust.resetButtonDisabledTooltip()} placement="top">
                    <Button size="small" disabled style={{width: "100%"}}>
                        {i18n.containers.payments.PaymentsListColumns.manualAdjust.resetButton()}
                    </Button>
                </Tooltip>
            ) : (
                <Popconfirm
                    title={
                        <div style={{maxWidth: 280}}>
                            <div style={{fontWeight: 600, marginBottom: 4}}>{i18n.containers.payments.PaymentsListColumns.manualAdjust.confirmTitle()}</div>
                            <div style={{fontSize: 12, color: "rgba(0,0,0,0.65)"}}>{i18n.containers.payments.PaymentsListColumns.manualAdjust.confirmHintEdited()}</div>
                        </div>
                    }
                    onConfirm={handleReset}
                    okText={i18n.containers.payments.PaymentsListColumns.manualAdjust.confirmOk()}
                    cancelText={i18n.containers.payments.PaymentsListColumns.manualAdjust.confirmCancel()}
                    okButtonProps={{loading, danger: true}}
                    placement="top"
                >
                    <Button size="small" danger style={{width: "100%"}} loading={loading}>
                        {i18n.containers.payments.PaymentsListColumns.manualAdjust.resetButton()}
                    </Button>
                </Popconfirm>
            )}
        </div>
    );

    return (
        <Popover content={popoverContent} trigger="click" placement="right" open={open} onOpenChange={setOpen} destroyTooltipOnHide>
            <span
                role="button"
                aria-label={i18n.containers.payments.PaymentsListColumns.manualAdjust.iconAriaLabel()}
                onClick={(e) => e.stopPropagation()}
                style={{cursor: "pointer", display: "inline-flex", alignItems: "center"}}
                data-testid={"ManualAdjustIndicator-" + record.id}
            >
                <PushpinOutlined style={{fontSize: 11, color: "#d4a017", opacity: 0.85}} />
            </span>
        </Popover>
    );
};

/**
 * Status-Indicator für die "tombstoneStatus"-Spalte.
 *
 * Reiner Anzeige-Tag — bei aktiven Posten (deletedAt == null) ein grünes
 * "Aktiv"-Tag, bei Tombstones (deletedAt != null) ein rotes "Gelöscht"-Tag
 * mit DeleteOutlined-Icon und einem Tooltip, der die Audit-Info zeigt
 * (Wer / Wann gelöscht). Die Restore-Aktion sitzt nicht hier, sondern in
 * der Action-Spalte (siehe RenderActions / RestoreActionButton) — dort
 * ersetzt der Wiederherstellen-Button die normalen Aktionen, weil ein
 * gelöschter Posten nicht editiert / bezahlt / erneut gelöscht werden kann.
 */
type TombstoneRecord = TableType & {
    deletedAt?: Maybe<Date | string>;
    deletedBy?: Maybe<{id: string; email: string}>;
};

export const TombstoneIndicator: FC<{record: TableType}> = ({record}) => {
    const tombstoneRecord = record as TombstoneRecord;
    const isTombstone = Boolean(tombstoneRecord.deletedAt);

    if (!isTombstone) {
        return (
            <Tag color="green" style={{margin: 0}}>
                {i18n.containers.payments.PaymentsListColumns.tombstone.tagActive()}
            </Tag>
        );
    }

    const deletedAtFormatted = moment(tombstoneRecord.deletedAt as Date | string).format("DD.MM.YYYY HH:mm");
    const userEmail = tombstoneRecord.deletedBy?.email ?? null;

    const auditLine = userEmail
        ? i18n.containers.payments.PaymentsListColumns.tombstone.auditWithUser({
              date: deletedAtFormatted,
              user: userEmail,
          })
        : i18n.containers.payments.PaymentsListColumns.tombstone.auditWithoutUser({
              date: deletedAtFormatted,
          });

    return (
        <Tooltip title={auditLine} placement="top">
            <Tag
                color="red"
                aria-label={i18n.containers.payments.PaymentsListColumns.tombstone.iconAriaLabel()}
                style={{margin: 0, display: "inline-flex", alignItems: "center", gap: 4}}
                data-testid={"TombstoneIndicator-" + record.id}
            >
                <DeleteOutlined style={{fontSize: 12}} />
                {i18n.containers.payments.PaymentsListColumns.tombstone.tagDeleted()}
            </Tag>
        </Tooltip>
    );
};

/**
 * Action-Spalten-Button für gelöschte Posten: ersetzt die normalen
 * Edit/SetPaid/Delete-Buttons durch einen einzigen "Wiederherstellen"-Button
 * mit Popconfirm. Refetch-Pattern identisch zu den anderen Mutations in
 * RenderActions, damit Summen und embedded Drawer-Listen synchron bleiben.
 */
export const RestoreActionButton: FC<{record: TableType; refetch?: () => void}> = ({record, refetch}) => {
    const refetchQueries = (): BaseMutationOptions["refetchQueries"] => [
        "GetBookingById",
        {query: GetBookingByIdDocument, variables: {id: record.booking.id}},
        "getUnpaidScheduledPayments",
        {query: GetUnpaidScheduledPaymentsDocument, variables: {bookingId: record.booking.id}},
        "getPaidScheduledPayments",
        {query: GetPaidScheduledPaymentsDocument, variables: {bookingId: record.booking.id}},
        "GetBookingByIdWithCourseLessonBookings",
        {query: GetBookingByIdWithCourseLessonBookingsDocument, variables: {id: record.booking.id}},
    ];

    const [restoreScheduledPayment, {loading}] = useRestoreScheduledPaymentMutation({
        refetchQueries: refetchQueries(),
        awaitRefetchQueries: true,
    });

    const handleRestore = async () => {
        try {
            await restoreScheduledPayment({variables: {id: record.id}});
            message.success(i18n.containers.payments.PaymentsListColumns.tombstone.successMessage(), 0.7);
            refetch?.();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            message.error(i18n.containers.payments.PaymentsListColumns.tombstone.errorMessage({error: errorMessage}), 1.5);
        }
    };

    return (
        <Popconfirm
            title={
                <div style={{maxWidth: 280}}>
                    <div style={{fontWeight: 600, marginBottom: 4}}>{i18n.containers.payments.PaymentsListColumns.tombstone.confirmTitle()}</div>
                    <div style={{fontSize: 12, color: "rgba(0,0,0,0.65)"}}>{i18n.containers.payments.PaymentsListColumns.tombstone.confirmHint()}</div>
                </div>
            }
            onConfirm={handleRestore}
            okText={i18n.containers.payments.PaymentsListColumns.tombstone.confirmOk()}
            cancelText={i18n.containers.payments.PaymentsListColumns.tombstone.confirmCancel()}
            okButtonProps={{loading, type: "primary"}}
            placement="left"
        >
            <Button size="small" type="primary" icon={<RollbackOutlined />} loading={loading} data-testid={"RestoreActionButton-" + record.id}>
                {i18n.containers.payments.PaymentsListColumns.tombstone.restoreButton()}
            </Button>
        </Popconfirm>
    );
};
